<template>
  <div class="price-page">
    <section class="hero">
      <div class="container">
        <p class="eyebrow">PRICING / PLANS</p>
        <h1>{{ texts.title }}</h1>
        <p class="lead">{{ texts.lead }}</p>
        <div class="hero-meta">
          <span>01</span>
          <p>{{ texts.planNote }}</p>
        </div>
      </div>
    </section>

    <section class="content">
      <div class="container">
        <div class="plan-summary">
          <article class="plan-card">
            <h2>{{ texts.freePlan.title }}</h2>
            <p class="plan-copy">{{ texts.freePlan.desc }}</p>
            <a class="btn btn-secondary" :href="freeTrialHref">{{ texts.freePlan.cta }}</a>
          </article>
          <article class="plan-card featured">
            <h2>{{ texts.yearlyPlan.title }}</h2>
            <p class="plan-copy">{{ texts.yearlyPlan.desc }}</p>
            <a class="btn btn-primary" :href="pricingInquiryHref">{{ texts.yearlyPlan.cta }}</a>
          </article>
        </div>

        <div class="table-card">
          <div class="table-wrap">
            <table class="pricing-table">
              <thead>
                <tr>
                  <th>{{ texts.table.feature }}</th>
                  <th>{{ texts.table.free }}</th>
                  <th v-for="tier in pricingTiers" :key="tier.orders">
                    <div class="tier-orders">{{ tier.orders }}</div>
                    <div class="tier-price">¥{{ tier.price.toLocaleString() }}</div>
                    <div class="tier-unit">¥{{ tier.unitPrice }}/{{ texts.table.orderUnit }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in texts.table.rows" :key="row.name">
                  <td class="feature-name">{{ row.name }}</td>
                  <td>{{ row.free }}</td>
                  <td v-for="(value, index) in row.paid" :key="`${row.name}-${index}`">{{ value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="faq-card">
          <h2>{{ texts.faqTitle }}</h2>
          <div class="faq-list">
            <article v-for="item in texts.faqs" :key="item.q" class="faq-item">
              <h3>{{ item.q }}</h3>
              <p>{{ item.a }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { localeState } from "@/utils/locale";

const baseTiers = [
  { orders: "12万单", ordersEn: "120k Orders", price: 10000, unitPrice: "0.083" },
  { orders: "30万单", ordersEn: "300k Orders", price: 20000, unitPrice: "0.067" },
  { orders: "50万单", ordersEn: "500k Orders", price: 30000, unitPrice: "0.060" },
  { orders: "70万单", ordersEn: "700k Orders", price: 40000, unitPrice: "0.057" },
  { orders: "100万单", ordersEn: "1M Orders", price: 50000, unitPrice: "0.050" },
  { orders: "130万单", ordersEn: "1.3M Orders", price: 60000, unitPrice: "0.046" },
];

export default {
  name: "PriceComparison",
  data() {
    return {
      copy: {
        zh: {
          title: "XINHE TRADING (HONGKONG) LIMITED价格方案",
          lead: "所有版本均包含核心系统能力与客户服务支持，帮助跨境团队更高效地管理订单、财务、广告与运营。",
          planNote: "按团队规模与年度订单量灵活选择，所有方案均支持 PC 与移动端。",
          freePlan: {
            title: "免费版",
            desc: "适合初步体验系统能力的团队，每月提供基础订单额度与标准功能。",
            cta: "免费使用",
          },
          yearlyPlan: {
            title: "包年版",
            desc: "适合稳定运营团队，提供更高订单容量、实施支持与高级分析能力。",
            cta: "立即购买",
          },
          table: {
            feature: "价格方案",
            free: "免费版",
            orderUnit: "单",
            rows: [
              { name: "订单额度", free: "5000单/月", paid: ["12万单/年", "30万单/年", "50万单/年", "70万单/年", "100万单/年", "130万单/年"] },
              { name: "实施服务", free: "线上直播培训", paid: ["线上线下实施", "线上线下实施", "线上线下实施", "线上线下实施", "线上线下实施", "线上线下实施"] },
              { name: "功能范围", free: "标准功能", paid: ["全功能", "全功能", "全功能", "全功能", "全功能", "全功能"] },
              { name: "数据看板", free: "基础版", paid: ["高级版", "高级版", "高级版", "高级版", "高级版", "高级版"] },
              { name: "售后保障", free: "基础支持", paid: ["60天无理由退款", "60天无理由退款", "60天无理由退款", "60天无理由退款", "60天无理由退款", "60天无理由退款"] },
            ],
          },
          faqTitle: "常见问题",
          faqs: [
            { q: "免费版和包年版有什么区别？", a: "免费版适合体验系统核心能力，包年版则提供更高订单容量、实施服务、退款保障和高级报表能力。" },
            { q: "包年版订单额度如何计算？", a: "包年版从合同生效之日起按年度计算，所购订单额度可在有效期内累计使用。" },
            { q: "是否支持 PC 和 APP 端使用？", a: "支持。不同版本都可以在 PC 和 APP 端使用，主要差异在订单量、服务与高级能力上。" },
          ],
          freeTrialSubject: "免费试用申请",
          pricingSubject: "价格方案咨询",
        },
        en: {
          title: "Hunan Panxian Intelligent Technology Pricing",
          lead: "Every plan includes core system capabilities and customer support to help cross-border teams manage orders, finance, advertising, and operations more efficiently.",
          planNote: "Choose by team scale and annual order volume. Every plan supports desktop and mobile access.",
          freePlan: {
            title: "Free Plan",
            desc: "A good fit for teams evaluating the platform, with a starter order quota and essential features each month.",
            cta: "Start Free",
          },
          yearlyPlan: {
            title: "Annual Plan",
            desc: "Designed for stable operating teams, with larger order capacity, implementation support, and advanced analytics.",
            cta: "Buy Now",
          },
          table: {
            feature: "Plan",
            free: "Free",
            orderUnit: "order",
            rows: [
              { name: "Order Capacity", free: "5,000 orders / month", paid: ["120k / year", "300k / year", "500k / year", "700k / year", "1M / year", "1.3M / year"] },
              { name: "Implementation Support", free: "Online live training", paid: ["Online + onsite support", "Online + onsite support", "Online + onsite support", "Online + onsite support", "Online + onsite support", "Online + onsite support"] },
              { name: "Feature Scope", free: "Standard features", paid: ["Full feature set", "Full feature set", "Full feature set", "Full feature set", "Full feature set", "Full feature set"] },
              { name: "Dashboards", free: "Basic", paid: ["Advanced", "Advanced", "Advanced", "Advanced", "Advanced", "Advanced"] },
              { name: "After-sales Support", free: "Basic support", paid: ["60-day refund guarantee", "60-day refund guarantee", "60-day refund guarantee", "60-day refund guarantee", "60-day refund guarantee", "60-day refund guarantee"] },
            ],
          },
          faqTitle: "FAQ",
          faqs: [
            { q: "What is the main difference between the free and annual plans?", a: "The free plan is for evaluating the platform, while the annual plan adds larger order capacity, implementation support, refund protection, and advanced reporting." },
            { q: "How is annual order capacity calculated?", a: "The annual plan is counted over the contract term, and purchased order capacity can be used cumulatively during the valid period." },
            { q: "Do all plans support both PC and app access?", a: "Yes. All plans support both PC and app usage. The main differences are order volume, service scope, and advanced capabilities." },
          ],
          freeTrialSubject: "Free trial request",
          pricingSubject: "Pricing inquiry",
        },
      },
    };
  },
  computed: {
    locale() {
      return localeState.locale;
    },
    texts() {
      return this.copy[this.locale] || this.copy.zh;
    },
    pricingTiers() {
      return baseTiers.map((tier) => ({
        ...tier,
        orders: this.locale === "en" ? tier.ordersEn : tier.orders,
      }));
    },
    freeTrialHref() {
      return `mailto:it_jch@xinhe.space?subject=${encodeURIComponent(this.texts.freeTrialSubject)}`;
    },
    pricingInquiryHref() {
      return `mailto:it_jch@xinhe.space?subject=${encodeURIComponent(this.texts.pricingSubject)}`;
    },
  },
};
</script>

<style scoped>
.price-page {
  background: #f5f8ff;
  min-height: 100%;
  padding-bottom: 4rem;
}
.hero {
  background: linear-gradient(135deg, #10214f, #1d4ed8 58%, #6da8ff);
  color: #fff;
  padding: 4.5rem 1.25rem 3.5rem;
}
.container {
  margin: 0 auto;
  max-width: 80rem;
}
.eyebrow {
  font-size: 0.875rem;
  letter-spacing: 0.12em;
  margin-bottom: 0.75rem;
  opacity: 0.82;
  text-transform: uppercase;
}
h1 {
  font-size: 2.25rem;
  line-height: 1.25;
  margin: 0;
}
.lead {
  font-size: 1.05rem;
  line-height: 1.9;
  margin-top: 1rem;
  max-width: 58rem;
}
.content {
  padding: 0 1.25rem;
}
.plan-summary {
  display: grid;
  gap: 1rem;
  margin-top: -2rem;
}
.plan-card,
.table-card,
.faq-card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 18px 48px rgba(28, 65, 148, 0.12);
}
.plan-card {
  padding: 1.5rem;
}
.plan-card.featured {
  background: linear-gradient(135deg, #173169, #2554fe);
  color: #fff;
}
.plan-card h2 {
  margin: 0 0 0.75rem;
}
.plan-copy {
  line-height: 1.8;
  margin: 0 0 1.25rem;
}
.btn {
  display: inline-block;
  border-radius: 999px;
  padding: 0.75rem 1.25rem;
  text-decoration: none;
}
.btn-primary {
  background: #fff;
  color: #173169;
}
.btn-secondary {
  border: 1px solid #2554fe;
  color: #2554fe;
}
.table-card,
.faq-card {
  margin-top: 1.25rem;
  padding: 1.5rem;
}
.table-wrap {
  overflow-x: auto;
}
.pricing-table {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
}
.pricing-table th,
.pricing-table td {
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 0.875rem;
  text-align: center;
  vertical-align: middle;
}
.pricing-table th:first-child,
.pricing-table td:first-child {
  text-align: left;
  min-width: 140px;
}
.tier-orders {
  color: #2554fe;
  font-weight: 600;
}
.tier-price {
  color: #173169;
  font-size: 1.1rem;
  font-weight: 700;
  margin-top: 0.5rem;
}
.tier-unit {
  color: #667085;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
.feature-name {
  color: #173169;
  font-weight: 600;
}
.faq-card h2 {
  color: #173169;
  margin: 0 0 1rem;
}
.faq-list {
  display: grid;
  gap: 1rem;
}
.faq-item {
  background: #f7faff;
  border: 1px solid #d8e5ff;
  border-radius: 0.875rem;
  padding: 1rem;
}
.faq-item h3 {
  color: #173169;
  margin: 0 0 0.5rem;
}
.faq-item p {
  color: #475467;
  line-height: 1.8;
  margin: 0;
}
@media (min-width: 1024px) {
  .hero {
    padding: 5.5rem 2rem 4.25rem;
  }
  .plan-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .plan-card,
  .table-card,
  .faq-card {
    padding: 2rem;
  }
}
</style>
<style scoped>
.price-page {
  background: #f3f0e8;
  min-height: 100%;
  overflow-x: hidden;
  padding-bottom: 0;
}
.hero {
  background: #102d26;
  color: #f6f3ea;
  min-height: 500px;
  overflow: hidden;
  padding: 92px 36px 100px;
  position: relative;
  text-align: left;
}
.hero::after {
  border: 1px solid rgba(237, 191, 94, 0.2);
  border-radius: 50%;
  content: "";
  height: 520px;
  position: absolute;
  right: -160px;
  top: -290px;
  width: 520px;
}
.container { max-width: 1280px; position: relative; z-index: 1; }
.hero .container {
  display: grid;
  gap: 18px 80px;
  grid-template-columns: minmax(0, 0.78fr) minmax(340px, 0.42fr);
}
.eyebrow {
  color: #edbf5e;
  font-size: 11px;
  font-weight: 700;
  grid-column: 1 / -1;
  letter-spacing: 0.16em;
  margin: 0;
  opacity: 1;
}
h1 {
  color: #f6f3ea;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 56px;
  font-weight: 400;
  line-height: 1.08;
  margin: 0;
  max-width: 760px;
}
.lead {
  color: rgba(246, 243, 234, 0.64);
  font-size: 15px;
  line-height: 1.85;
  margin: 24px 0 0;
  max-width: 680px;
}
.hero-meta {
  align-self: end;
  border-top: 1px solid rgba(246, 243, 234, 0.2);
  grid-column: 2;
  grid-row: 2 / 4;
  padding-top: 22px;
}
.hero-meta span {
  color: #edbf5e;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 32px;
}
.hero-meta p { color: rgba(246, 243, 234, 0.58); font-size: 13px; line-height: 1.75; margin: 14px 0 0; }
.content { padding: 0 36px 112px; }
.plan-summary {
  border-bottom: 1px solid #c8cbc2;
  border-top: 1px solid #c8cbc2;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: -42px;
}
.plan-card,
.table-card,
.faq-card {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}
.plan-card {
  background: #fbfaf6;
  min-height: 270px;
  padding: 42px 46px;
  text-align: left;
}
.plan-card + .plan-card { border-left: 1px solid #c8cbc2; }
.plan-card.featured {
  background: #edbf5e;
  color: #102d26;
}
.plan-card::before {
  color: #b36b33;
  content: "PLAN / 01";
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  margin-bottom: 22px;
}
.plan-card.featured::before { color: rgba(16, 45, 38, 0.65); content: "PLAN / 02"; }
.plan-card h2 {
  color: #17352d;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 32px;
  font-weight: 400;
  margin: 0 0 15px;
}
.plan-copy { color: #707973; font-size: 13px; line-height: 1.75; margin: 0 0 24px; max-width: 480px; }
.featured .plan-copy { color: rgba(16, 45, 38, 0.72); }
.btn {
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  padding: 12px 18px;
}
.btn-secondary { border: 1px solid #17352d; color: #17352d; }
.btn-primary { background: #17352d; color: #f6f3ea; }
.table-card {
  border-bottom: 1px solid #c8cbc2;
  margin-top: 84px;
  padding: 0;
  text-align: left;
}
.table-card::before {
  color: #b36b33;
  content: "PLAN MATRIX / 02";
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  margin-bottom: 20px;
}
.table-wrap {
  border-top: 1px solid #c8cbc2;
  overflow-x: auto;
}
.pricing-table { background: transparent; min-width: 1080px; }
.pricing-table th,
.pricing-table td {
  border-bottom: 1px solid #d2d4cd;
  border-right: 1px solid #d2d4cd;
  color: #68736c;
  font-size: 12px;
  line-height: 1.55;
  padding: 19px 16px;
}
.pricing-table th:first-child,
.pricing-table td:first-child {
  background: #e5e7e0;
  border-left: 1px solid #d2d4cd;
  min-width: 150px;
  position: sticky;
  left: 0;
  z-index: 2;
}
.pricing-table th { background: #fbfaf6; color: #17352d; }
.tier-orders { color: #b36b33; font-size: 11px; letter-spacing: 0.03em; }
.tier-price {
  color: #17352d;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 20px;
  font-weight: 400;
  margin-top: 8px;
}
.tier-unit { color: #78817b; font-size: 10px; }
.feature-name { color: #17352d; font-size: 12px; font-weight: 700; }
.faq-card {
  display: grid;
  gap: 50px;
  grid-template-columns: 0.42fr 1fr;
  margin-top: 100px;
  padding: 0;
  text-align: left;
}
.faq-card h2 {
  color: #17352d;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 46px;
  font-weight: 400;
  margin: 0;
}
.faq-card h2::before {
  color: #b36b33;
  content: "QUESTIONS / 03";
  display: block;
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  margin-bottom: 22px;
}
.faq-list { border-top: 1px solid #c8cbc2; gap: 0; }
.faq-item {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #c8cbc2;
  border-radius: 0;
  padding: 23px 0 22px 48px;
  position: relative;
}
.faq-item::before {
  color: #b36b33;
  content: "?";
  font-family: Georgia, "Times New Roman", serif;
  font-size: 24px;
  left: 10px;
  position: absolute;
  top: 17px;
}
.faq-item h3 { color: #17352d; font-size: 14px; font-weight: 650; margin: 0 0 8px; }
.faq-item p { color: #747d77; font-size: 12px; line-height: 1.75; }

@media (max-width: 900px) {
  .hero { min-height: auto; padding: 72px 20px 82px; }
  .hero .container { grid-template-columns: 1fr; }
  h1 { font-size: 39px; overflow-wrap: anywhere; }
  .hero-meta { grid-column: 1; grid-row: auto; margin-top: 24px; }
  .content { padding: 0 20px 76px; }
  .plan-summary { grid-template-columns: 1fr; margin-top: -30px; }
  .plan-card { min-height: 0; padding: 34px 25px; }
  .plan-card + .plan-card { border-left: 0; border-top: 1px solid #c8cbc2; }
  .table-card { margin-top: 66px; }
  .table-wrap { margin-right: -20px; }
  .faq-card { gap: 32px; grid-template-columns: 1fr; margin-top: 72px; }
  .faq-card h2 { font-size: 38px; }
}
</style>
